import React, { useState, useCallback } from "react";
import {
  ChevronDown,
  Calendar,
  CheckSquare,
  Circle,
  CheckCircle,
  ArrowLeft,
  ImageIcon,
} from "lucide-react";

// ─── Text block previews ───

const PreviewHeading = ({ comp }) => (
  <p className="text-[14px] font-bold text-gray-900 mb-2">{comp.text}</p>
);

const PreviewSubheading = ({ comp }) => (
  <p className="text-[12px] font-semibold text-gray-600 mb-2">{comp.text}</p>
);

const PreviewBody = ({ comp }) => (
  <p className="text-[11px] text-gray-500 leading-relaxed mb-3">{comp.text}</p>
);

const PreviewCaption = ({ comp }) => (
  <p className="text-[10px] text-gray-400 mb-2">{comp.text}</p>
);

const PreviewRichText = ({ comp }) => {
  const html = (comp.text || "")
    .replace(/\*([^*]+)\*/g, "<strong>$1</strong>");
  return (
    <p
      className="text-[11px] text-gray-600 leading-relaxed mb-3"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const PreviewImage = ({ comp }) => (
  <div className="mb-3">
    {comp.src ? (
      <img
        src={comp.src}
        alt="Flow image"
        className="w-full rounded-lg object-cover"
        style={{ maxHeight: comp.height || 200 }}
        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
      />
    ) : null}
    <div
      className="w-full bg-gray-100 rounded-lg flex items-center justify-center text-gray-400"
      style={{ height: comp.height || 200, display: comp.src ? "none" : "flex" }}
    >
      <ImageIcon size={24} />
    </div>
  </div>
);

// ─── Input previews ───

const PreviewTextInput = ({ comp }) => {
  const isPassword = comp["input-type"] === "password";
  return (
    <div className="mb-3">
      <label className="text-[11px] font-medium text-gray-700 mb-0.5 flex items-center gap-1">
        {comp.label || "Input"}
        {comp.required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={isPassword ? "password" : "text"}
        placeholder={comp.label || ""}
        readOnly
        className="w-full px-3 py-2 text-[12px] bg-white border border-gray-200 rounded-lg text-gray-400"
      />
    </div>
  );
};

const PreviewDropdown = ({ comp }) => (
  <div className="mb-3">
    <label className="text-[11px] font-medium text-gray-700 mb-0.5 block">{comp.label || "Select"}</label>
    <div className="w-full px-3 py-2 text-[12px] bg-white border border-gray-200 rounded-lg text-gray-400 flex items-center justify-between">
      <span>Select...</span>
      <ChevronDown size={13} className="text-gray-400" />
    </div>
  </div>
);

const PreviewRadio = ({ comp }) => (
  <div className="mb-3">
    <label className="text-[11px] font-medium text-gray-700 mb-1 block">{comp.label || "Choose"}</label>
    <div className="space-y-1.5">
      {(comp["data-source"] || []).map((opt, i) => (
        <label key={opt.id || i} className="flex items-center gap-2 cursor-default">
          <Circle size={14} className="text-gray-300" />
          <span className="text-[12px] text-gray-600">{opt.title}</span>
        </label>
      ))}
    </div>
  </div>
);

const PreviewCheckbox = ({ comp }) => (
  <div className="mb-3">
    <label className="text-[11px] font-medium text-gray-700 mb-1 block">{comp.label || "Select"}</label>
    <div className="space-y-1.5">
      {(comp["data-source"] || []).map((opt, i) => (
        <label key={opt.id || i} className="flex items-center gap-2 cursor-default">
          <CheckSquare size={14} className="text-gray-300" />
          <span className="text-[12px] text-gray-600">{opt.title}</span>
        </label>
      ))}
    </div>
  </div>
);

const PreviewDatePicker = ({ comp }) => (
  <div className="mb-3">
    <label className="text-[11px] font-medium text-gray-700 mb-0.5 block">{comp.label || "Date"}</label>
    <div className="w-full px-3 py-2 text-[12px] bg-white border border-gray-200 rounded-lg text-gray-400 flex items-center justify-between">
      <span>YYYY-MM-DD</span>
      <Calendar size={13} className="text-gray-400" />
    </div>
  </div>
);

// ─── Footer preview ───

const PreviewFooter = ({ comp, onAction }) => (
  <button
    onClick={() => onAction(comp["on-click-action"])}
    className="w-full mt-2 py-2.5 bg-[#00a884] text-white text-[13px] font-semibold rounded-lg hover:bg-[#009677] transition-colors"
  >
    {comp.label || "Continue"}
  </button>
);

// ─── Unified renderer ───

const renderComponent = (comp, onAction) => {
  const key = comp._uid || comp.name || Math.random();
  switch (comp.type) {
    case "TextHeading": return <PreviewHeading key={key} comp={comp} />;
    case "TextSubheading": return <PreviewSubheading key={key} comp={comp} />;
    case "TextBody": return <PreviewBody key={key} comp={comp} />;
    case "TextCaption": return <PreviewCaption key={key} comp={comp} />;
    case "RichText": return <PreviewRichText key={key} comp={comp} />;
    case "Image": return <PreviewImage key={key} comp={comp} />;
    case "TextInput": return <PreviewTextInput key={key} comp={comp} />;
    case "Dropdown": return <PreviewDropdown key={key} comp={comp} />;
    case "RadioButtonsGroup": return <PreviewRadio key={key} comp={comp} />;
    case "CheckboxGroup": return <PreviewCheckbox key={key} comp={comp} />;
    case "DatePicker": return <PreviewDatePicker key={key} comp={comp} />;
    case "Footer": return <PreviewFooter key={key} comp={comp} onAction={onAction} />;
    default: return null;
  }
};

// ─── Main preview ───

const FlowPreview = ({ flow }) => {
  const screens = flow?.screens || [];
  const [currentIdx, setCurrentIdx] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [history, setHistory] = useState([]);

  const currentScreen = screens[currentIdx];

  const handleAction = useCallback(
    (action) => {
      if (!action) return;
      if (action.name === "complete") { setCompleted(true); return; }
      if (action.name === "navigate" && action.next?.name) {
        const targetIdx = screens.findIndex((s) => s.id === action.next.name);
        if (targetIdx >= 0) {
          setHistory((prev) => [...prev, currentIdx]);
          setCurrentIdx(targetIdx);
        }
      }
    },
    [screens, currentIdx]
  );

  const goBack = useCallback(() => {
    if (history.length > 0) {
      setCurrentIdx(history[history.length - 1]);
      setHistory((h) => h.slice(0, -1));
      setCompleted(false);
    }
  }, [history]);

  const reset = useCallback(() => {
    setCurrentIdx(0);
    setHistory([]);
    setCompleted(false);
  }, []);

  if (!currentScreen) {
    return <div className="text-center py-12 text-gray-400 text-xs">No screens to preview</div>;
  }

  const layoutChildren = currentScreen.layout?.children || [];

  return (
    <div className="flex flex-col h-full">
      <div className="mx-auto w-full max-w-[320px]">
        {/* Phone header */}
        <div className="bg-[#075e54] rounded-t-2xl px-4 py-3 flex items-center gap-3">
          {history.length > 0 && (
            <button onClick={goBack} className="text-white/80 hover:text-white"><ArrowLeft size={16} /></button>
          )}
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">WA</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[12px] font-semibold truncate">{currentScreen.title || currentScreen.id}</p>
            <p className="text-white/60 text-[10px]">
              Screen {screens.findIndex((s) => s.id === currentScreen.id) + 1} of {screens.length}
              {currentScreen.terminal && " · Final"}
            </p>
          </div>
        </div>

        {/* Screen content */}
        <div className="bg-[#e5ddd5] min-h-[380px] max-h-[480px] overflow-y-auto p-4">
          {completed ? (
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle size={24} className="text-green-500" />
              </div>
              <p className="text-sm font-semibold text-gray-800 mb-1">Flow Completed</p>
              <p className="text-xs text-gray-500 mb-4">The form has been submitted successfully.</p>
              <button onClick={reset} className="px-4 py-1.5 text-xs font-medium text-[#048AAD] border border-[#048AAD] rounded-lg hover:bg-[#048AAD]/5">
                Restart Preview
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 space-y-0">
                {layoutChildren.map((child) => {
                  if (child.type === "Form") {
                    return (
                      <div key={child._uid || child.name}>
                        {(child.children || []).map((fc) => renderComponent(fc, handleAction))}
                      </div>
                    );
                  }
                  return renderComponent(child, handleAction);
                })}
              </div>
            </div>
          )}
        </div>

        {/* Phone bottom */}
        <div className="bg-[#f0f2f5] rounded-b-2xl px-4 py-2 flex items-center justify-center">
          <div className="flex gap-1">
            {screens.map((s, i) => (
              <div key={s.id} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === currentIdx ? "bg-[#075e54]" : "bg-gray-300"}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlowPreview;
